import React from "react"
import {
	Flex,
	Text,
	Rte,
	INNER_MAX_WIDTH,
	READABLE_LINE_MAX_WIDTH,
	colors, DynamicLink, scale
} from "@capioweb/ui-components/src"
import {
	ContentAreaProperty,
	ContentAreaPropertyItem,
	XhtmlString,
} from "../../@types/CapioWeb/core"
import {
	LinkListProperty,
	LinkProperty,
	StringProperty,
} from "../../@types/EpiFoundationSpa/Property"
import {
	PageBaseProps,
	BreadcrumbItemProps,
} from "../../components/Core/pageBase"
import { BlockResolver, BreadcrumbsBlock } from "../../components"
import { EpiContext, LocalesContext } from "../../context"
import { shouldRender, createMarkup } from "../../utils"
import styled from "styled-components"
import { scrollToAnchor } from "../../utils/scrollToAnchor"
import { useLocation } from "react-router-dom"

interface CapioNexusDiagnosisPageProps extends PageBaseProps {
	header: StringProperty
	preamble?: XhtmlString
	body?: XhtmlString
	mainContentArea: ContentAreaProperty
	cta?: ContentAreaProperty
	breadcrumb: Array<BreadcrumbItemProps>
	relatedDiagnoses?: ContentAreaProperty
	reviewer: string
	lastReviewDate: string
	references: LinkListProperty
}

interface ListItemProps extends ContentAreaPropertyItem {
	href: string
	text: string
}
const UlStyled = styled.ul`
	width: 100%;
	flex-wrap: wrap;
	display: flex;
	flex-direction: row;

	& a {
		margin-bottom: 0;
		text-decoration: underline;
	}
`

const LiStyled = styled.li`
	list-style: disc;
	position: relative;
	left: ${scale.px(3)};
	margin-bottom: 0;
	line-height: 24px;
	width: fit-content;

	& a {
		display: block;
	}

	& :hover {
		text-decoration: underline;
	}
`
const CapioNexusDiagnosisPage: React.FC<CapioNexusDiagnosisPageProps> = (
	props
) => {
	const { editMode } = React.useContext(EpiContext)
	const { translate, translationKeys } = React.useContext(LocalesContext)

	const showReferenceNote =
		props.reviewer || props.references?.value?.length > 0

	const { hash } = useLocation()

	const id = decodeURI(hash.substring(1))

	const el = document?.getElementById(id)

	React.useEffect(() => {
		if (el) {
			//Scroll to anchorID position if it exists
			scrollToAnchor(el)
		}
	}, [el]) //UseEffect will run again when 'el' is ready

	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={props.breadcrumb} />
			<Flex as="section" w={1} px={3}>
				<Flex
					w={1}
					flexDirection="column"
					maxw={INNER_MAX_WIDTH}
					style={{ margin: "0 auto" }}
				>
					<Flex
						w={1}
						flexDirection={{ _: "column", l: "row" }}
						columnGap={{ l: 3 }}
						py={4}
					>
						<Flex
							w={1}
							flex={{ _: 1, l: 8 / 12 }}
							flexDirection="column"
							maxw={READABLE_LINE_MAX_WIDTH}
						>
							{props.header?.value && (
								<Text variant="headline-1">{props.header.value}</Text>
							)}

							{props.preamble?.value && (
								<Text variant="preamble">
									<span
										dangerouslySetInnerHTML={createMarkup(props.preamble.value)}
									></span>
								</Text>
							)}

							{props.cta?.value && (
								<Flex
									w="auto"
									mb={3}
									display={{ _: "flex", l: "none" }}
									flexDirection="column"
									rowGap={2}
								>
									<BlockResolver blocks={props.cta?.value} />
								</Flex>
							)}

							{props.body?.value && (
								<Rte>
									<div
										dangerouslySetInnerHTML={createMarkup(props.body.value)}
									></div>
								</Rte>
							)}

							{props.relatedDiagnoses?.value?.length > 0 && (
								<Flex
									p={{ _: 3, s: 4 }}
									backgroundColor="purple33"
									justifyContent="center"
									flexDirection="column"
									alignItems="flex-start"
									style={{ borderRadius: "24px" }}
									w={1}
									mt={4}
								>
									<Text variant="headline-3">
										{translate(translationKeys.relatedDiagnoses)}
									</Text>
									<UlStyled>
										{props.relatedDiagnoses.value.map((item: ListItemProps) => {
											return (
												<Flex
													as="li"
													display="list-item"
													w={{ _: "100%", m: "calc(50% - 16px)" }}
													mb={0.5}
													key={item.contentLink?.id}
												>
													<Text.Link
														to={item?.href ?? "#"}
														key={item.contentLink?.id}
														underline
													>
														{item.text}
													</Text.Link>
												</Flex>
											)
										})}
									</UlStyled>
								</Flex>
							)}
						</Flex>
						<Flex
							flexDirection="column"
							w={1}
							mb={3}
							flex={{ _: 1, l: 4 / 12 }}
							alignSelf="flex-start"
							display={{ _: "none", l: "block" }}
						>
							<Flex mb={1}>
								{props.cta?.value && (
									<Flex
										w={1}
										mb={3}
										flexDirection="column"
										rowGap={2}
									>
										<BlockResolver blocks={props.cta?.value} />
									</Flex>
								)}
							</Flex>
						</Flex>
					</Flex>
					{showReferenceNote && (
						<Flex
							flexDirection="column"
							pt={2}
							mb={5}
							borderTop
							borderColor='blue33'
							maxw={READABLE_LINE_MAX_WIDTH}
						>
							{props.reviewer && (
								<Flex
									justifyContent="space-between"
									columnGap={8}
									flexDirection={{ _: "column", m: "row" }}
								>
									<Flex flexDirection="column">
										<Text mb={0} fontWeight="bold">
											{translate(translationKeys.reviewer)}:
										</Text>
										<Text>{props.reviewer}</Text>
									</Flex>
									{props.lastReviewDate && (
										<Flex flexDirection="column" minw="fit-content">
											<Text mb={0} fontWeight="bold">
												{translate(translationKeys.lastReviewDate)}:
											</Text>
											<Text>{props.lastReviewDate}</Text>
										</Flex>
									)}
								</Flex>
							)}
							{props.references?.value?.length > 0 && (
								<Flex flexDirection="column" position="relative">
									<Text mb={0} fontWeight="bold">
										{translate(translationKeys.references)}:
									</Text>
									<Flex as="ul" flexDirection="column">
										{props.references.value.map((item: LinkProperty) => {
											return (
												<LiStyled key={item?.contentLink?.id}>
													<DynamicLink
														url={item?.href}
														title={item?.title}
														target={item?.target}
													>
														{item.text}
													</DynamicLink>
												</LiStyled>
											)
										})}
									</Flex>
								</Flex>
							)}
						</Flex>
					)}
				</Flex>
			</Flex>
			{shouldRender(editMode.inEditMode, props.mainContentArea?.value) && (
				<div data-epi-edit={"mainContentArea"}>
					<BlockResolver blocks={props.mainContentArea?.value} />
				</div>
			)}
		</Flex>
	)
}

export { CapioNexusDiagnosisPage }
